import React from 'react';
import { format, parseISO, isAfter, isBefore, addDays, differenceInDays } from 'date-fns';

// Date formatting utilities
export const formatDate = (date, formatStr = 'MMM dd, yyyy') => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, formatStr);
};

export const formatDateTime = (date) => {
  if (!date) return '-';
  const dateObj = typeof date === 'string' ? parseISO(date) : date;
  return format(dateObj, 'MMM dd, yyyy HH:mm');
};

export const isOverdue = (dueDate) => {
  if (!dueDate) return false;
  const due = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
  return isBefore(due, new Date());
};

export const isDueSoon = (dueDate, days = 4) => {
  if (!dueDate) return false;
  const due = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
  const soonDate = addDays(new Date(), days);
  return isBefore(due, soonDate) && isAfter(due, new Date());
};

export const getDaysUntilDue = (dueDate) => {
  if (!dueDate) return null;
  const due = typeof dueDate === 'string' ? parseISO(dueDate) : dueDate;
  return differenceInDays(due, new Date());
};

// Status utilities
export const getStatusBadge = (status) => {
  const statusConfig = {
    pending: { label: 'Pending', className: 'bg-yellow-100 text-yellow-800' },
    'in-progress': { label: 'In Progress', className: 'bg-blue-100 text-blue-800' },
    completed: { label: 'Completed', className: 'bg-green-100 text-green-800' },
    overdue: { label: 'Overdue', className: 'bg-red-100 text-red-800' },
    verified: { label: 'Verified', className: 'bg-green-100 text-green-800' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

export const getVerificationStatusBadge = (status) => {
  const statusConfig = {
    pending: { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-800' },
    approved: { label: 'Approved', className: 'bg-green-100 text-green-800' },
    rejected: { label: 'Rejected', className: 'bg-red-100 text-red-800' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-100 text-gray-800' };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

// Frequency utilities
export const getFrequencyLabel = (frequency) => {
  const frequencyMap = {
    daily: 'Daily',
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    '6 month': '6 Month',
    'half-yearly': 'Half-Yearly',
    yearly: 'Yearly',
    '2 year': '2 Year',
    '3 year': '3 Year',
    '4 year': '4 Year',
    '5 year': '5 Year',
  };
  return frequencyMap[frequency] || frequency;
};

// Role utilities
export const getRoleLabel = (role) => {
  const roleMap = {
    admin: 'Admin',
    superAdmin: 'Super Admin',
    plantAdmin: 'Plant Admin',
    employee: 'Employee',
  };
  return roleMap[role] || role;
};

export const getUserTypeLabel = (userType) => {
  const userTypeMap = {
    internal: 'Internal',
    external: 'External',
    contractor: 'Contractor',
    'plant-head': 'Plant Head',
    'plant-hr': 'Plant HR',
    employee: 'Employee',
  };
  return userTypeMap[userType] || userType;
};

// Data transformation utilities
export const transformComplianceData = (compliance) => {
  if (!compliance) return null;

  return {
    ...compliance,
    formattedDueDate: formatDate(compliance.dueDate),
    formattedCreatedAt: formatDateTime(compliance.createdAt),
    formattedUpdatedAt: formatDateTime(compliance.updatedAt),
    isOverdue: isOverdue(compliance.dueDate),
    isDueSoon: isDueSoon(compliance.dueDate),
    daysUntilDue: getDaysUntilDue(compliance.dueDate),
    statusBadge: getStatusBadge(compliance.status),
    verificationStatusBadge: getVerificationStatusBadge(compliance.verificationStatus),
    frequencyLabel: getFrequencyLabel(compliance.frequency),
  };
};

export const transformUserData = (user) => {
  if (!user) return null;

  return {
    ...user,
    roleLabel: getRoleLabel(user.role),
    userTypeLabel: getUserTypeLabel(user.userType),
    formattedCreatedAt: formatDateTime(user.createdAt),
  };
};

export const getAttachmentUrl = (url) => {
  if (!url) return '';
  
  if (url.includes('res.cloudinary.com') && url.includes('fl_attachment/')) {
    return url.replace('fl_attachment/', '');
  }
  
  return url;
};

// Pagination utilities
export const getPaginationInfo = (data, currentPage, pageSize) => {
  const totalItems = data?.total || 0;
  const totalPages = Math.ceil(totalItems / pageSize);
  const hasNextPage = currentPage < totalPages;
  const hasPrevPage = currentPage > 1;

  return {
    currentPage,
    totalPages,
    totalItems,
    hasNextPage,
    hasPrevPage,
    startItem: (currentPage - 1) * pageSize + 1,
    endItem: Math.min(currentPage * pageSize, totalItems),
  };
};

// Filter utilities
export const buildFilterParams = (filters) => {
  const params = {};

  // Parameter mapping for backend compatibility
  const paramMapping = {
    search: 'search', // or 'q' if backend expects 'q'
    role: 'role',
    plant: 'plant',
    isActive: 'isActive', // or 'status' if backend expects 'status'
  };

  Object.keys(filters).forEach(key => {
    if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
      const paramKey = paramMapping[key] || key;
      let value = filters[key];

      // Convert boolean values to strings if needed
      if (typeof value === 'boolean') {
        value = value.toString();
      }

      params[paramKey] = value;
    }
  });

  console.log('buildFilterParams input:', filters);
  console.log('buildFilterParams output:', params);
  return params;
};

// File utilities
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const validateFileType = (file, allowedTypes) => {
  return allowedTypes.includes(file.type);
};

export const validateFileSize = (file, maxSize) => {
  return file.size <= maxSize;
};

// Search utilities
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Validation utilities
export const validateEmail = (email) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

export const validatePassword = (password) => {
  return password.length >= 6;
};

export const validateOTP = (otp) => {
  return /^\d{6}$/.test(otp);
};

// Error handling utilities
export const getErrorMessage = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

// User Data Utilities
export const getUserData = () => {
  try {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  } catch (error) {
    console.error('Error reading user data from localStorage:', error);
    localStorage.removeItem('user');
    return null;
  }
};

export const getUserRole = () => {
  const user = getUserData();
  return user?.role || null;
};

export const getUserPlant = () => {
  const user = getUserData();
  return user?.plant || null;
};

export const getUserPlantName = () => {
  const user = getUserData();
  return user?.plantName || null;
};

export const isUserAdmin = () => {
  const user = getUserData();
  return user?.role === 'admin';
};

export const isUserSuperAdmin = () => {
  const user = getUserData();
  return user?.role === 'superAdmin';
};

export const isUserPlantAdmin = () => {
  const user = getUserData();
  return user?.role === 'plantAdmin';
};

export const isUserEmployee = () => {
  const user = getUserData();
  return user?.role === 'employee';
};

// Local Storage Utilities
export const storage = {
  setUser: (userData) => {
    try {
      localStorage.setItem('user', JSON.stringify(userData));
    } catch (error) {
      console.error('Error saving user to localStorage:', error);
    }
  },

  getUser: () => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch (error) {
      console.error('Error reading user from localStorage:', error);
      localStorage.removeItem('user');
      return null;
    }
  },

  removeUser: () => {
    try {
      localStorage.removeItem('user');
    } catch (error) {
      console.error('Error removing user from localStorage:', error);
    }
  },

  clearAll: () => {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  }
};

// API Response Utilities
export const extractArrayData = (data) => {
  // Handle different possible response structures
  if (Array.isArray(data)) {
    return data;
  }

  if (data && Array.isArray(data.data)) {
    return data.data;
  }

  if (data && Array.isArray(data.plants)) {
    return data.plants;
  }

  if (data && Array.isArray(data.users)) {
    return data.users;
  }

  if (data && Array.isArray(data.compliances)) {
    return data.compliances;
  }

  // If no array found, return empty array
  return [];
}; 